import { useState } from 'react';
import { useNavigate } from 'react-router';

import type {
  ClientFormValues,
  ModalOpenerType,
  OtpFormValues
} from '@/components/Shared/entities/client-otp-modal';
import { ROUTES } from '@/components/Shared/routes/routes';
import { generateRandomSixDiginCode } from '@/services';
import { onlyDigits } from '@/services/formatter';
import { useAppDispatch, useAppSelector } from '@/store';
import { onSetSelectedBankToInitial } from '@/store/financingStep.slice';
import {
  setCurrent,
  setClientInfo as storeSetClientInfo
} from '@/store/newOrder.slice';
import {
  onSetOrderOtpCode,
  requestCreateUnverifiedDraft,
  retryValidateOTP,
  validateOTP
} from '@/store/orderStep.slice';
import type { TDataSource } from '@/types/dataSource';

interface ValidateModalInterface {
  openedModal: ModalOpenerType;
  defaultPhone?: string;
  uuid?: string;
  clientComment?: string;
}

interface IProps {
  getRequestsPerPage?: () => void;
}

const BMG_ERROR_MESSAGE =
  'Введенный номер телефона не зарегистрирован на ИИН в Базе Мобильных Граждан';

const useValidateAutoCreditOtp = ({ getRequestsPerPage }: IProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { dealer } = useAppSelector((state) => state.userInfo);

  const [isValidateModalOpened, setIsValidateModalOpened] =
    useState<ValidateModalInterface>({ openedModal: '' });

  const [clientInfo, setClientInfo] = useState<ClientFormValues | null>(null);
  const [requestId, setRequestId] = useState('');

  const onClientSubmit = async (values: ClientFormValues) => {
    setClientInfo(values);

    const splittedFullName = values.fullName
      .replace(/\s{2,}/g, ' ')
      .split(' ')
      .filter((data) => data.trim() !== '');
    const [lastName, firstName, ...restPartOfName] = splittedFullName;
    const clientAutoCreditInfo = {
      iin: values.iin,
      lastName,
      firstName,
      middleName: restPartOfName ? restPartOfName.join(' ') : ' ',
      telNum: values.phone,
      id: generateRandomSixDiginCode()
    };
    dispatch(storeSetClientInfo(clientAutoCreditInfo));

    const createRequestDraftDto = {
      iin: values.iin,
      lastName: lastName || ' ',
      firstName: firstName || ' ',
      middleName: restPartOfName ? restPartOfName.join(' ') : ' ',
      clientPhone: onlyDigits(values.phone),
      type: values.sentWithType,
      dealer_uuid: dealer?.value || ''
    };

    const response = await dispatch(
      requestCreateUnverifiedDraft(createRequestDraftDto)
    );

    if (requestCreateUnverifiedDraft.fulfilled.match(response)) {
      setRequestId(response.payload);
      getRequestsPerPage?.();
      return '';
    }

    const errorMessage = response.error.message;
    if (errorMessage?.includes(BMG_ERROR_MESSAGE)) {
      setIsValidateModalOpened({ openedModal: 'bmg-error' });
    }

    return response.error?.message || 'Произошла ошибка! Попробуйте позже';
  };

  const onOtpSubmit = async (values: OtpFormValues) => {
    const code = values.first + values.second + values.third + values.fourth;
    const response = await dispatch(
      validateOTP({ otp: code, uuid: requestId })
    );

    if (validateOTP.fulfilled.match(response) && response.payload?.result) {
      dispatch(onSetSelectedBankToInitial());
      dispatch(onSetOrderOtpCode(code));
      dispatch(setCurrent(0));
      navigate(ROUTES.autoCreditCreate, {
        state: { requestId }
      });
      return '';
    }

    if (validateOTP.rejected.match(response) && response.error) {
      if (response.error.message === 'Too many otp attempts') {
        getRequestsPerPage?.();
        return 'Превышено количество попыток ввода кода подтверждения';
      } else {
        return 'Произошла ошибка! Попробуйте позже';
      }
    }

    if (validateOTP.fulfilled.match(response) && response.payload?.message) {
      return `Неверный код подтверждения! ${response.payload?.message}`;
    }

    return `Неверный код подтверждения!`;
  };

  const retryOTP = async (data?: {
    phone: string;
    type: 'WhatsApp' | 'SMS';
    uuid?: string;
  }): Promise<void> => {
    await dispatch(
      retryValidateOTP({
        clientPhone: onlyDigits(clientInfo?.phone || data?.phone || ''),
        type: data?.type || 'WhatsApp',
        uuid: requestId || data?.uuid || ''
      })
    );
  };

  const continueVerifying = async (row: TDataSource) => {
    setRequestId(row.id);
    setIsValidateModalOpened({
      openedModal: 'otp-form',
      defaultPhone: row.client.telNumber,
      uuid: row.id
    });
  };

  return {
    onClientSubmit,
    onOtpSubmit,
    retryOTP,
    continueVerifying,
    isValidateModalOpened,
    setIsValidateModalOpened
  };
};

export default useValidateAutoCreditOtp;
